import { Box, Button, Checkbox, HStack, Text, VStack } from "@chakra-ui/react"
import { formatDistanceStrict } from "date-fns"
import { useContext, useState } from "react"
import AppColors from "../../../../constants/AppColors"
import { DotMenuIcon, RebuttalIcon } from "../../../../constants/IconData"
import { useComponentVisible } from "../../../../hooks"
import { AuditSheetContext } from "../../../../providers/AuditSheetProvider"
import CommentInput from "./CommentInput"
import CommentSaveButton from "./CommentSaveButton"
import CustomMenu from "./CustomMenu"
import { getTaggedUser } from '../../getTaggedUsers';
import { useToastr } from '@laxmimanogna/code-quick-components';
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons"
import RenderMessage from "../RenderMessage"
import { successToast } from "../../../../components/successToast"
import { RESOLVED_STATUS, REBUTTAL_CLOSED_STATUS, dynamicAuditColumnHeader } from "../../constants"

const CommentBox = ({ 
    onEditModalOpen, 
    onDeleteModalOpen, 
    comment, 
    index, 
    selectedComment, 
    setSelectedComment, 
    setSelectedParentComment,
    batchId = undefined
}) => {

    const [hiddenBoxKey, setHiddenBoxKey] = useState('')
    const auditSheetContext = useContext(AuditSheetContext);
    const toast = useToastr();

    const { setSelectedCell, postComment, isPostCommentLoading, allComments, setAllComments, allUsers, commentFlags, setCommentFlags, sheetData, setCurrentUpload, currentUpload } =
        auditSheetContext;
    const isRowWise = comment.audit_sheet_rows?.length > 0 && !comment.audit_sheet_columns?.length;
    const isRebuttal = comment.action === 'REBUTTAL';

    const getType = () => {
        if (comment.action === 'BATCH NOTES') return 'Batch Note';
        if (!comment.audit_sheet_rows?.length && !comment.audit_sheet_columns?.length) return 'Note';
        if (isRebuttal) return 'Rebuttal';
        return 'Comment';
    }

    const getStatus = () => {
        const replies = comment.reply_comments || [];
        let status = 'Open';
        for (const reply of replies) {
            const action = reply.action;
            if (action === 'REBUTTAL-ACCEPTED') status = 'Accepted';
            else if (action === 'REBUTTAL-REJECTED') status = 'Rejected';
            else if (action === 'REBUTTAL-RESOLVED') status = 'Closed';
            else if (RESOLVED_STATUS.includes(action)) status = 'Resolved';
            else if (action === 'RE-OPENED') status = isRebuttal ? 'Accepted' : 'Re-opened';
            // OPEN replies are just messages — they don't change thread status
        }
        return status;
    }

    const threadStatus = getStatus();
    const isRejected = threadStatus === 'Rejected';
    const isClosed = threadStatus === 'Closed' || threadStatus === 'Resolved';
    const isAccepted = threadStatus === 'Accepted';

    const [replyMessage, setReplyMessage] = useState('')
    const [isInternal, setIsInternal] = useState(false)
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);

    const onEditClick = (currentComment) => {
        setSelectedComment(currentComment)
        if (comment.id !== currentComment.id) {
            setSelectedParentComment(comment)
        } else {
            setSelectedParentComment({})
        }
        onEditModalOpen()
        setIsComponentVisible(false)
    }

    const onDeleteClick = (currentComment) => {
        setSelectedComment(currentComment)
        if (comment.id !== currentComment.id) {
            setSelectedParentComment(comment)
        } else {
            setSelectedParentComment({})
        }
        onDeleteModalOpen()
        setIsComponentVisible(false)
    }

    const onReplyClick = async (currentComment) => {
        setSelectedComment(currentComment)
        const input = document.getElementById(`${index}-mention-input`);
        input.focus();
        setIsComponentVisible(false)
    }

    // Non-rebuttal resolve (Notes/Comments) — unchanged
    const onResolveClick = async (currentComment) => {
        setSelectedComment(currentComment)
        const { audit_sheet_columns, audit_sheet_rows } = comment
        const updateBody = {
            audit_sheet_columns,
            audit_sheet_rows,
            chart: comment.chart,
            action: 'RESOLVED',
            parent: comment.id,
        }
        setIsComponentVisible(false)
        const response = await postComment(updateBody)
        if (response.id) {
            setAllComments(prev => prev.map(c =>
                c.id === comment.id
                    ? { ...c, reply_comments: [...(c.reply_comments || []), response] }
                    : c
            ));
            const updatedFlags = { ...commentFlags };
            comment.audit_sheet_rows.forEach(rowId => {
                updatedFlags[rowId] = { ...(commentFlags[rowId] || {}) };
                if (comment.audit_sheet_columns?.length) {
                    comment.audit_sheet_columns.forEach(col => { updatedFlags[rowId][col] = false; });
                } else {
                    updatedFlags[rowId]['__row__'] = false;
                }
            });
            setCommentFlags(updatedFlags);
        }
    }

    const onReopenClick = async (currentComment) => {
        setSelectedComment(currentComment)
        setIsComponentVisible(false)
        const { audit_sheet_columns, audit_sheet_rows } = comment
        const updateBody = {
            audit_sheet_columns,
            audit_sheet_rows,
            chart: comment.chart,
            action: 'RE-OPENED',
            parent: comment.id,
        }
        const response = await postComment(updateBody)
        if (response.id) {
            setAllComments(prev => prev.map(c =>
                c.id === comment.id
                    ? { ...c, reply_comments: [...(c.reply_comments || []), response] }
                    : c
            ));
            const updatedFlags = { ...commentFlags };
            comment.audit_sheet_rows.forEach(rowId => {
                updatedFlags[rowId] = { ...(commentFlags[rowId] || {}) };
                if (comment.audit_sheet_columns?.length) {
                    comment.audit_sheet_columns.forEach(col => { updatedFlags[rowId][col] = true; });
                } else {
                    updatedFlags[rowId]['__row__'] = true;
                }
            });
            setCommentFlags(updatedFlags);
        }
    }

    // QA accepts rebuttal — correction needed by auditor
    const onAcceptClick = async () => {
        const { audit_sheet_columns, audit_sheet_rows } = comment
        const updateBody = {
            audit_sheet_columns,
            audit_sheet_rows,
            chart: comment.chart,
            action: 'REBUTTAL-ACCEPTED',
            parent: comment.id,
        }
        const response = await postComment(updateBody)
        if (response.id) {
            setAllComments(prev => prev.map(c =>
                c.id === comment.id
                    ? { ...c, reply_comments: [...(c.reply_comments || []), response] }
                    : c
            ));
            // Mark flag as open (still needs auditor correction)
            const updatedFlags = { ...commentFlags };
            comment.audit_sheet_rows.forEach(rowId => {
                updatedFlags[rowId] = { ...(commentFlags[rowId] || {}), '__row__': true };
            });
            setCommentFlags(updatedFlags);
            const description = currentUpload?.status === 'CLIENT REBUTTAL'
                ? 'Rebuttal accepted — send to auditor to make corrections.'
                : 'Rebuttal accepted';
            successToast(toast, { header: 'Success!', description });
        }
    }

    // QA rejects rebuttal — original finding stands, thread closed
    const onRejectClick = async () => {
        const { audit_sheet_columns, audit_sheet_rows } = comment
        const updateBody = {
            audit_sheet_columns,
            audit_sheet_rows,
            chart: comment.chart,
            action: 'REBUTTAL-REJECTED',
            parent: comment.id,
        }
        const response = await postComment(updateBody)
        if (response.id) {
            setAllComments(prev => prev.map(c =>
                c.id === comment.id
                    ? { ...c, reply_comments: [...(c.reply_comments || []), response] }
                    : c
            ));
            // Mark flag as resolved (rejected = closed)
            const updatedFlags = { ...commentFlags };
            comment.audit_sheet_rows.forEach(rowId => {
                updatedFlags[rowId] = { ...(commentFlags[rowId] || {}) };
                if (comment.audit_sheet_columns?.length) {
                    comment.audit_sheet_columns.forEach(col => { updatedFlags[rowId][col] = false; });
                } else {
                    updatedFlags[rowId]['__row__'] = false;
                }
            });
            setCommentFlags(updatedFlags);
            successToast(toast, { header: 'Success!', description: 'Rebuttal rejected.' });
        }
    }

    // QA reopens a closed rebuttal — goes back to Accepted, auditor must correct again
    const onReopenRebuttalClick = async () => {
        const { audit_sheet_columns, audit_sheet_rows } = comment
        const updateBody = {
            audit_sheet_columns,
            audit_sheet_rows,
            chart: comment.chart,
            action: 'RE-OPENED',
            parent: comment.id,
        }
        const response = await postComment(updateBody)
        if (response?.id) {
            setAllComments(prev => prev.map(c =>
                c.id === comment.id
                    ? { ...c, reply_comments: [...(c.reply_comments || []), response] }
                    : c
            ));
            const updatedFlags = { ...commentFlags };
            comment.audit_sheet_rows.forEach(rowId => {
                updatedFlags[rowId] = { ...(commentFlags[rowId] || {}) };
                updatedFlags[rowId]['__row__'] = true;
                if (comment.audit_sheet_columns?.length) {
                    comment.audit_sheet_columns.forEach(col => { updatedFlags[rowId][col] = true; });
                }
            });
            setCommentFlags(updatedFlags);
            const description = currentUpload?.status === 'CLIENT REBUTTAL'
                ? 'Rebuttal reopened — send to auditor to make corrections.'
                : 'Rebuttal reopened — auditor will make corrections.';
            successToast(toast, { header: 'Success!', description });
        }
    }

    // QA or Auditor closes an accepted rebuttal after correction
    const onCloseClick = async () => {
        const { audit_sheet_columns, audit_sheet_rows } = comment
        const updateBody = {
            audit_sheet_columns,
            audit_sheet_rows,
            chart: comment.chart,
            action: 'REBUTTAL-RESOLVED',
            parent: comment.id,
        }
        const response = await postComment(updateBody)
        if (response.id) {
            setAllComments(prev => prev.map(c =>
                c.id === comment.id
                    ? { ...c, reply_comments: [...(c.reply_comments || []), response] }
                    : c
            ));
            const updatedFlags = { ...commentFlags };
            comment.audit_sheet_rows.forEach(rowId => {
                updatedFlags[rowId] = { ...(commentFlags[rowId] || {}) };
                updatedFlags[rowId]['__row__'] = false;
                if (comment.audit_sheet_columns?.length) {
                    comment.audit_sheet_columns.forEach(col => { updatedFlags[rowId][col] = false; });
                }
            });
            setCommentFlags(updatedFlags);
            successToast(toast, { header: 'Success!', description: 'Rebuttal closed.' });
        }
    }

    const replyAction = async () => {
        const taggedUsers = getTaggedUser(replyMessage)
        const { audit_sheet_columns, audit_sheet_rows } = comment
        const updateBody = {
            audit_sheet_columns,
            audit_sheet_rows,
            chart: comment.chart,
            tagged_user: taggedUsers,
            comment: replyMessage,
            action: 'OPEN',
            parent: comment.id,
            is_internal: isInternal,
        }
        const response = await postComment(updateBody)
        if (response.id) {
            setReplyMessage('')
            setIsInternal(false)
            successToast(toast, { header: 'Success!', description: 'Reply sent.' })
            setAllComments(prev => prev.map(c =>
                c.id === comment.id
                    ? { ...c, reply_comments: [...(c.reply_comments || []), { ...response }] }
                    : c
            ));
        }
    }

    // For non-rebuttal threads — existing resolve/reopen buttons
    const showResolveButton = (commentData) => {
        if (isRebuttal) return false;
        if (!commentData.audit_sheet_rows?.length && !commentData.audit_sheet_columns?.length) return false;
        if (!commentData.isParent) return false;
        const replyLength = commentData.reply_comments.length;
        return !(replyLength >= 1 && RESOLVED_STATUS.includes(commentData.reply_comments[replyLength - 1].action));
    }

    const showReopenButton = (commentData) => {
        if (isRebuttal) return false;
        if (!commentData.audit_sheet_rows?.length && !commentData.audit_sheet_columns?.length) return false;
        if (!commentData.isParent) return false;
        const replyLength = commentData.reply_comments.length;
        return replyLength >= 1 && RESOLVED_STATUS.includes(commentData.reply_comments[replyLength - 1].action);
    }

    const statusBadgeColor = () => {
        if (threadStatus === 'Accepted') return '#16a34a';
        if (threadStatus === 'Rejected') return AppColors.danger;
        if (isClosed) return 'rgba(255,255,255,0.15)';
        return 'rgba(255,255,255,0.25)';
    }

    const bodyBgColor = () => {
        if (isClosed || isRejected) return AppColors.loginFormBg;
        return 'white';
    }

    const headerBgColor = () => {
        if (comment.action === 'BATCH NOTES') return '#d97706';
        if (getType() === 'Note') return AppColors.yellowColor;
        if (isRejected || isClosed) return AppColors.darkGray;
        return AppColors.secondary;
    }

    const renderDotMenu = (commentData) => (
        <Box
            position='relative'
            p={1}
            flexShrink={0}
            onClick={() => { setSelectedComment(commentData) }}
        >
            <DotMenuIcon style={{ height: 14, width: 14, cursor: 'pointer' }} onClick={() => {
                setIsComponentVisible(true)
            }} />
            {isComponentVisible && selectedComment.key === `${commentData.index}-${commentData.i}-inline-comment` ? <CustomMenu
                onEditClick={() => onEditClick(commentData)}
                onDeleteClick={() => onDeleteClick(commentData)}
                onReplyClick={() => onReplyClick(commentData)}
                onResolveClick={() => onResolveClick(commentData)}
                onReopenClick={() => onReopenClick(commentData)}
                comment={commentData}
                ref={ref}
            /> : null}
        </Box>
    )

    const renderCommentHeader = (commentData) => {
        const actionButtons = [];
        if (!commentData.justStatus) {
            if (isRebuttal && commentData.isParent && threadStatus === 'Open') {
                actionButtons.push(
                    <Button key='accept' size='xs' variant='solid' colorScheme='green' borderRadius='full'
                        onClick={(e) => { e.stopPropagation(); onAcceptClick(); }}>
                        Accept
                    </Button>,
                    <Button key='reject' size='xs' variant='outline' colorScheme='red' borderRadius='full'
                        onClick={(e) => { e.stopPropagation(); onRejectClick(); }}>
                        Reject
                    </Button>
                );
            }
            if (isRebuttal && commentData.isParent && threadStatus === 'Closed') {
                actionButtons.push(
                    <Button key='reopen-rebuttal' size='xs' variant='outline' colorScheme='orange' borderRadius='full'
                        onClick={(e) => { e.stopPropagation(); onReopenRebuttalClick(); }}>
                        Re-open
                    </Button>
                );
            }
            if (showResolveButton(commentData)) {
                actionButtons.push(
                    <Button key='resolve' size='xs' variant='outline' colorScheme='green' borderRadius='full'
                        onClick={(e) => { e.stopPropagation(); onResolveClick(commentData); }}>
                        Resolve
                    </Button>
                );
            }
            if (showReopenButton(commentData)) {
                actionButtons.push(
                    <Button key='reopen' size='xs' variant='outline' colorScheme='gray' borderRadius='full'
                        onClick={(e) => { e.stopPropagation(); onReopenClick(commentData); }}>
                        Re-open
                    </Button>
                );
            }
        }

        return (
            <VStack align='stretch' spacing={1}>
                <HStack justifyContent='space-between' align='flex-start'>
                    <HStack spacing={1} align='baseline' flexWrap='wrap'>
                        <Text fontSize='sm' fontWeight='semibold' color={AppColors.secondary} lineHeight='1.3'>
                            {commentData?.user?.first_name} {commentData?.user?.last_name}
                        </Text>
                        <Text fontSize='xs' color='gray.400'>
                            {commentData.commented_date
                                ? formatDistanceStrict(new Date(commentData.commented_date), new Date(), { addSuffix: true })
                                : ''}
                        </Text>
                        {commentData.is_internal && (
                            <Box px={1.5} py='1px' bg='purple.100' borderRadius='full' border='1px solid' borderColor='purple.200'>
                                <Text fontSize='9px' fontWeight='bold' color='purple.600' letterSpacing='wide'>INTERNAL</Text>
                            </Box>
                        )}
                    </HStack>
                    {/* Hide three-dots on the main/parent rebuttal comment */}
                    {!commentData.justStatus && !(commentData.isParent && comment.action === 'REBUTTAL') && renderDotMenu(commentData)}
                </HStack>
                {actionButtons.length > 0 && (
                    <HStack spacing={1} flexWrap='wrap'>
                        {actionButtons}
                    </HStack>
                )}
            </VStack>
        );
    }

    const renderComment = (obj) => {
        const STATUS = {
            'RE-OPENED': isRebuttal ? 'Reopened' : 'Re-opened',
            'RESOLVED': 'Marked as resolved',
            'REBUTTAL-RESOLVED': 'Closed',
            'REBUTTAL-ACCEPTED': 'Accepted — correction in progress',
            'REBUTTAL-REJECTED': 'Rejected',
        }
        return (
            <Box
                key={obj.key}
                px={3} py={2}
                bg={obj.isParent ? bodyBgColor() : 'gray.50'}
                borderTop={obj.isParent ? 'none' : '1px solid'}
                borderColor='gray.100'
            >
                {renderCommentHeader(obj)}
                {obj.justStatus
                    ? <Text as='i' fontSize='xs' color={AppColors.darkGray}>{STATUS[obj.status]}</Text>
                    : <RenderMessage comment={obj} allUsers={allUsers} />
                }
            </Box>
        )
    }

    const isCollapsed = hiddenBoxKey === `${index}-comment-box`

    return <Box mb={4} key={`${index}-comment-box`}
        cursor={comment?.audit_sheet_rows?.length ? 'pointer' : 'default'}
        onClick={() => {
            if (comment?.audit_sheet_rows && comment?.audit_sheet_columns) {
                setSelectedCell({ row: comment.audit_sheet_rows[0], col: comment.audit_sheet_columns[0] })
            }
        }}
    >
        <Box borderRadius='lg' shadow='sm' border='1px solid' borderColor='gray.200'>
            <HStack
                borderTopRadius='lg'
                px={3} py={1.5}
                bg={headerBgColor()}
                justifyContent='space-between'
                cursor='pointer'
                onClick={(e) => {
                    e.stopPropagation()
                    setHiddenBoxKey(isCollapsed ? '' : `${index}-comment-box`)
                }}
            >
                <HStack spacing={2} flex={1} minW={0} overflow='hidden'>
                    <Box px={2} py='1px' bg='rgba(0,0,0,0.18)' borderRadius='full' flexShrink={0}>
                        <Text fontSize='10px' fontWeight='bold' color='white' letterSpacing='wide' whiteSpace='nowrap'>
                            {getType().toUpperCase()}
                        </Text>
                    </Box>
                    {batchId && comment.action !== 'BATCH NOTES' && comment.file_name && (
                        <Text fontSize='xs' color='rgba(255,255,255,0.9)' fontWeight='medium' noOfLines={1} minW={0}>
                            {comment.file_name}
                        </Text>
                    )}
                    {getType() !== 'Note' && getType() !== 'Batch Note' && sheetData[comment.audit_sheet_rows?.[0]] && (
                        <Text fontSize='xs' color='rgba(255,255,255,0.8)' noOfLines={1} minW={0}>
                            {isRowWise
                                ? `Row ${sheetData[comment.audit_sheet_rows[0]]}`
                                : `${dynamicAuditColumnHeader[comment.audit_sheet_columns[0]] || comment.audit_sheet_columns[0]} · Row ${sheetData[comment.audit_sheet_rows[0]]}`
                            }
                        </Text>
                    )}
                </HStack>
                <HStack spacing={1.5} flexShrink={0}>
                    {/* No status badge for Notes or Batch Notes */}
                    {getType() !== 'Note' && getType() !== 'Batch Note' && (
                        <Box px={2} py='1px' borderRadius='full' bg={statusBadgeColor()}>
                            <Text fontSize='xs' color='white' whiteSpace='nowrap'>
                                {threadStatus}
                            </Text>
                        </Box>
                    )}
                    {isCollapsed
                        ? <ChevronDownIcon cursor='pointer' color='white' />
                        : <ChevronUpIcon cursor='pointer' color='white' />
                    }
                </HStack>
            </HStack>
            {!isCollapsed && <Box>
                {renderComment({
                    ...comment,
                    isParent: true,
                    justStatus: false,
                    status: comment.action,
                    key: `${index}-${-1}-inline-comment`,
                    index,
                    i: -1
                })}
                {comment.reply_comments?.length ? comment.reply_comments.map((reply, i) => {
                    return renderComment({
                        ...reply,
                        isParent: false,
                        justStatus: !reply.comment,
                        status: reply.action,
                        index,
                        i,
                        key: `${index}-${i}-inline-comment`,
                    })
                }) : null}
                {comment.audit_sheet_rows.length ? <Box px={3} pb={3} pt={2} borderTop='1px solid' borderColor='gray.100'>
                    {isRejected ? (
                        <HStack pl={3} py={'3px'} borderLeft='3px solid' borderColor={AppColors.danger} spacing={1}>
                            <Text fontSize='xs' fontWeight='semibold' color={AppColors.danger}>Rebuttal rejected — original finding stands</Text>
                        </HStack>
                    ) : threadStatus === 'Resolved' ? (
                        <HStack pl={3} py={'3px'} borderLeft='3px solid' borderColor={AppColors.darkGray} spacing={1}>
                            <Text fontSize='xs' fontWeight='semibold' color={AppColors.darkGray}>Rebuttal closed</Text>
                        </HStack>
                    ) : (
                        <>
                            {isAccepted && (
                                <HStack mb={2} pl={3} py={'3px'} borderLeft='3px solid' borderColor='#d97706' spacing={1}>
                                    <Text fontSize='xs' fontWeight='semibold' color='#b7791f'>
                                        {currentUpload?.status === 'CLIENT REBUTTAL'
                                            ? 'Accepted — send to auditor to make corrections'
                                            : 'Accepted — waiting for auditor correction'}
                                    </Text>
                                </HStack>
                            )}
                            <CommentInput
                                message={replyMessage}
                                setMessage={setReplyMessage}
                                id={`${index}-mention-input`}
                                placeholder='Add a note...'
                            />
                            <Checkbox
                                size='sm'
                                colorScheme='purple'
                                mt={1}
                                isChecked={isInternal}
                                onChange={e => setIsInternal(e.target.checked)}
                            >
                                <Text fontSize='xs' color='gray.500'>Mark Internal</Text>
                            </Checkbox>
                            {replyMessage ? <CommentSaveButton onSave={() => replyAction()} onCancel={() => setReplyMessage('')} isLoading={isPostCommentLoading} /> : null}
                            {isAccepted && (
                                <HStack justify='center' mt={2}>
                                    <Button
                                        size='xs'
                                        colorScheme='green' variant='outline'
                                        borderRadius='full'
                                        onClick={(e) => { e.stopPropagation(); onCloseClick(); }}
                                    >
                                        Resolve Thread
                                    </Button>
                                </HStack>
                            )}
                        </>
                    )}
                </Box> : null}
            </Box>}
        </Box>
    </Box>
}
export default CommentBox
